import { 
  collection, 
  addDoc,
  doc,
  getDocs,
  updateDoc,
  deleteDoc,
  query, 
  where,
  orderBy,
  onSnapshot,
  limit
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION = 'notifications';

/**
 * Tipos de notificação:
 * - 'appointment_created': Cliente criou agendamento
 * - 'appointment_confirmed': Barbearia confirmou agendamento
 * - 'appointment_cancelled': Agendamento cancelado
 * - 'appointment_reminder': Lembrete de agendamento próximo
 */

/**
 * Criar nova notificação
 */
export const createNotification = async (notificationData) => {
  try {
    const docRef = await addDoc(collection(db, COLLECTION), {
      ...notificationData,
      read: false,
      createdAt: new Date().toISOString()
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error creating notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar notificações de um usuário
 */
export const getNotificationsByUserId = async (userId, limitCount = 50) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    const querySnapshot = await getDocs(q);
    const notifications = [];
    
    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: notifications };
  } catch (error) {
    console.error('Error getting notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Buscar notificações não lidas de um usuário
 */
export const getUnreadNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('userId', '==', userId),
      where('read', '==', false),
      orderBy('createdAt', 'desc')
    );
    const querySnapshot = await getDocs(q);
    const notifications = [];
    
    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });
    
    return { success: true, data: notifications };
  } catch (error) {
    console.error('Error getting unread notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Marcar notificação como lida
 */
export const markNotificationAsRead = async (notificationId) => {
  try {
    const docRef = doc(db, COLLECTION, notificationId);
    await updateDoc(docRef, {
      read: true,
      readAt: new Date().toISOString()
    });
    return { success: true };
  } catch (error) {
    console.error('Error marking notification as read:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Marcar todas as notificações como lidas
 */
export const markAllNotificationsAsRead = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION), 
      where('userId', '==', userId),
      where('read', '==', false)
    );
    const querySnapshot = await getDocs(q);
    
    const promises = [];
    querySnapshot.forEach((document) => {
      promises.push(
        updateDoc(doc(db, COLLECTION, document.id), {
          read: true,
          readAt: new Date().toISOString()
        })
      );
    });
    
    await Promise.all(promises);
    return { success: true };
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Deletar notificação
 */
export const deleteNotification = async (notificationId) => {
  try {
    const docRef = doc(db, COLLECTION, notificationId);
    await deleteDoc(docRef);
    return { success: true };
  } catch (error) {
    console.error('Error deleting notification:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Listener em tempo real para notificações do usuário
 */
export const subscribeToUserNotifications = (userId, callback) => {
  const q = query(
    collection(db, COLLECTION), 
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  
  return onSnapshot(q, (querySnapshot) => {
    const notifications = [];
    querySnapshot.forEach((doc) => {
      notifications.push({ id: doc.id, ...doc.data() });
    });
    callback(notifications);
  }, (error) => {
    console.error('Error in notifications listener:', error);
    callback([]);
  });
};

/**
 * Helper: Criar notificação quando agendamento é criado
 */
export const notifyAppointmentCreated = async (appointmentData, barbershopOwnerUserId) => {
  const formattedDate = new Date(appointmentData.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const notification = {
    userId: barbershopOwnerUserId,
    type: 'appointment_created',
    title: 'Novo Agendamento!',
    message: `Você recebeu um novo agendamento para ${appointmentData.service} no dia ${formattedDate} às ${appointmentData.time}.`,
    data: {
      appointmentId: appointmentData.id,
      barbershopId: appointmentData.barbershopId,
      service: appointmentData.service,
      date: appointmentData.date,
      time: appointmentData.time
    }
  };
  
  return createNotification(notification);
};

/**
 * Helper: Criar notificação quando agendamento é confirmado
 */
export const notifyAppointmentConfirmed = async (appointmentData, clientUserId) => {
  const formattedDate = new Date(appointmentData.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const notification = {
    userId: clientUserId,
    type: 'appointment_confirmed',
    title: 'Agendamento Confirmado! ✅',
    message: `Seu agendamento para ${appointmentData.service} no dia ${formattedDate} às ${appointmentData.time} foi confirmado!`,
    data: {
      appointmentId: appointmentData.id,
      barbershopId: appointmentData.barbershopId,
      service: appointmentData.service,
      date: appointmentData.date,
      time: appointmentData.time
    }
  };
  
  return createNotification(notification);
};

/**
 * Apagar todas as notificações de um usuário
 */
export const deleteAllNotifications = async (userId) => {
  try {
    const q = query(
      collection(db, COLLECTION),
      where('userId', '==', userId)
    );
    
    const snapshot = await getDocs(q);
    
    const deletePromises = snapshot.docs.map(doc => 
      deleteDoc(doc.ref)
    );
    
    await Promise.all(deletePromises);
    
    return { success: true, count: snapshot.docs.length };
  } catch (error) {
    console.error('Error deleting all notifications:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Helper: Criar notificação quando agendamento é cancelado
 */
export const notifyAppointmentCancelled = async (appointmentData, userId, cancelledBy) => {
  const isCancelledByOwner = cancelledBy === 'owner';
  const formattedDate = new Date(appointmentData.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  
  const notification = {
    userId: userId,
    type: 'appointment_cancelled',
    title: 'Agendamento Cancelado ❌',
    message: isCancelledByOwner
      ? `Seu agendamento para ${appointmentData.service} no dia ${formattedDate} às ${appointmentData.time} foi cancelado pela barbearia.`
      : `O agendamento de ${appointmentData.service} no dia ${formattedDate} às ${appointmentData.time} foi cancelado pelo cliente.`,
    data: {
      appointmentId: appointmentData.id,
      barbershopId: appointmentData.barbershopId,
      service: appointmentData.service,
      date: appointmentData.date,
      time: appointmentData.time,
      cancelledBy
    }
  };
  
  return createNotification(notification);
};
